/**
 * Two structurally-disjoint step-up detectors, both duplicated-literal (not
 * imported) because wagepro-web must not depend on `apps/api` per
 * docs/architecture-boundaries.md — see
 * `apps/ledger-web/src/routes/onboarding/steps/subscribe_client_state.ts`
 * for the established prior-art pattern of duplicating a wire-shape literal
 * with a comment pointing at its source of truth.
 *
 * - {@link isStepUpRequired} — the WRITE-gate. `apps/api/src/lib/
 *   assert_money_write_allowed.ts` rejects a money-write mutation (approve
 *   pay run, record/void a statutory payment, …) with `UNAUTHORIZED` +
 *   `data.ledgerCode === 'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED'` when the
 *   session that minted the token wasn't established with two-factor
 *   authentication.
 * - {@link isAal2Required} — the READ-gate (C7 PR B, #21938).
 *   `requireAal2` (`apps/api/src/trpc/init.ts`) rejects a read procedure —
 *   `personalPayroll.listEmployerLinks`/`listPayslips`/`getPayslip` today —
 *   with `FORBIDDEN` + `data.aal2Required === true` under the exact same
 *   AAL2 condition, but via a completely different wire field and a
 *   different tRPC error code. Neither detector's field ever appears on the
 *   other's rejection: a write-gate FORBIDDEN never carries `aal2Required`,
 *   and a read-gate FORBIDDEN never carries `ledgerCode ===
 *   'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED'`. See `step_up.test.ts`'s
 *   "mutual exclusion" block, which asserts this both ways off a single
 *   real wire-shape fixture.
 *
 * `isAal2Required` re-derives the check that ships as prior art in
 * `apps/tax-web/src/screens/ess_accept_logic.ts`'s `isAal2RequiredError`
 * (`data?.aal2Required === true` against the SAME `requireAal2` middleware,
 * over a real `createTRPCProxyClient`) rather than importing it — cross-app
 * imports between sibling product apps violate
 * docs/architecture-boundaries.md the same way an `apps/api` import would.
 *
 * `isStepUpRequired` IS wired (#22821): `data/use_pay_runs.ts`'s
 * `approvePayRun`/`startPayRun` both classify a real
 * `assertMoneyWriteAllowed` AAL2 rejection via this function, and their
 * callers (`wizard_steps.tsx`'s `ApprovalStep` for sign-off,
 * `run_payroll_screen.tsx`'s empty-state action for start) render dedicated
 * 2FA guidance off that classification. `StepUpDialog`
 * (`components/auth/step_up_dialog.tsx`) remains unwired today — both call
 * sites render the guidance inline rather than through that dialog.
 * `isAal2Required`, similarly, IS wired (`data/use_my_pay.ts`) — it gates
 * genuinely real read procedures.
 *
 * KNOWN GAP (accepted, narrowed but NOT closed — issue #22046): a fresh
 * `beginSignIn()` round trip (via {@link restartSignIn} below) DOES pick up
 * an AAL2-upgraded session — C7 PR A's `totpEnable` re-mint
 * (`apps/api/src/trpc/routers/auth.ts:2522`) means the common "enrol 2FA,
 * then retry" loop now genuinely completes. What remains open: a refresh
 * grant carries `auth_strength` forward from the stored token row without
 * re-resolving the live session, so an AAL1 token that merely refreshes
 * (never re-authorizes) stays AAL1 forever. Only a fresh authorize round
 * trip — not a bare re-query — observes an upgraded session. See
 * `my_pay_step_up_panel.tsx`'s "Sign in again" handler comment, and
 * `wagepro_store_my_pay.ts` §3 in the PR body for the full trace. This gap
 * is orthogonal to `StepUpDialog`'s still-unresolved issue below.
 *
 * KNOWN GAP (accepted, deferred — issue #22046, not fixed by this pass):
 * `StepUpDialog`'s "Sign in again" button cannot actually force a fresh
 * two-factor login today — see the comment above that button's onClick
 * handler in `step_up_dialog.tsx` for the full explanation. This is harmless
 * while the dialog stays unwired (as it is now), but MUST be resolved before
 * wiring it to any real `assertMoneyWriteAllowed`-gated mutation
 * (`payroll.approvePayRun`, `recordStatutoryPayment`,
 * `voidStatutoryPayment`, …) — otherwise the button silently does nothing
 * useful and the AAL2 step-up control has no real way forward for the user.
 */
import { beginSignIn, signOut } from './oauth_pkce.js';

/**
 * `assertMoneyWriteAllowed` (apps/api/src/lib/assert_money_write_allowed.ts)
 * attaches this literal string to the UNAUTHORIZED error's `data.ledgerCode`
 * via `ErrorCode.MONEY_WRITE_STEP_UP_REQUIRED`
 * (apps/api/src/lib/error_codes.ts).
 */
const MONEY_WRITE_STEP_UP_LEDGER_CODE =
  'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED';

/** Machine-readable fields lifted off a caught tRPC error's `.data`. */
interface TrpcErrorDataLike {
  ledgerCode?: string | null;
  aal2Required?: boolean | null;
}

function hasTrpcErrorData(value: unknown): value is { data: unknown } {
  return typeof value === 'object' && value !== null && 'data' in value;
}

function isTrpcErrorDataLike(value: unknown): value is TrpcErrorDataLike {
  return typeof value === 'object' && value !== null;
}

/**
 * Whether `err` is the AAL2 step-up-required rejection from a money-write
 * mutation. Narrows via plain shape-checking (not `err instanceof
 * TRPCClientError`) so it works against both a real `TRPCClientError` and
 * the `{ data: { ledgerCode } }`-shaped errors already used elsewhere in
 * this app's tests and sibling apps (see `auth_context.ts`'s
 * `isAuthRejectionError` for the `TRPCClientError`-specific variant).
 */
export function isStepUpRequired(err: unknown): boolean {
  if (!hasTrpcErrorData(err)) return false;
  const data = err.data;
  if (!isTrpcErrorDataLike(data)) return false;
  return data.ledgerCode === MONEY_WRITE_STEP_UP_LEDGER_CODE;
}

/**
 * Whether `err` is the AAL2 step-up-required rejection from a `requireAal2`
 * read-gated procedure (`personalPayroll.listEmployerLinks`/`listPayslips`/
 * `getPayslip`). The real `errorFormatter` (`apps/api/src/trpc/init.ts`)
 * emits `data.aal2Required: aal2Gate?.aal2Required ?? null` on EVERY error
 * response, so `null` (not absent) is what a non-matching rejection actually
 * looks like on the wire — the check below MUST be `=== true`, never a bare
 * truthiness check (which would also pass for a `data.aal2Required: null`
 * global-gate rejection, `undefined`, or any other truthy-adjacent
 * mis-shape) and never `'aal2Required' in data` (`null` is a present key).
 * See `step_up.test.ts`'s explicit `null`-vs-absent case.
 */
export function isAal2Required(err: unknown): boolean {
  if (!hasTrpcErrorData(err)) return false;
  const data = err.data;
  if (!isTrpcErrorDataLike(data)) return false;
  return data.aal2Required === true;
}

/**
 * Signs out of wagepro-web's local token pair, then immediately begins a
 * fresh authorize round trip. This IS the working completion of the
 * enrol-2FA-then-retry loop for the AAL2 read-gate (see this module's
 * docblock) — unlike `StepUpDialog`'s still-broken "Sign in again" button
 * (issue #22046, a DIFFERENT gap: the write-path dialog's session never
 * actually got re-minted at AAL2 server-side), a fresh `beginSignIn()` here
 * genuinely observes C7 PR A's `totpEnable` re-mint. It does NOT fix the
 * narrower residual gap (a refresh grant alone never upgrades AAL — only
 * this fresh authorize round trip does; see the module docblock's "narrowed
 * but NOT closed" note).
 *
 * `signOut()` never throws (see `oauth_pkce.ts`) and `beginSignIn()`
 * full-page-redirects, so there is nothing meaningful for a caller to await
 * or recover from — same "best-effort, fire-and-forget" contract
 * `step_up_dialog.tsx`'s own (unwired) handler already documented; lifted
 * out here so `MyPayStepUpPanel` doesn't have to re-derive it.
 */
export function restartSignIn(): void {
  void signOut().then(() => beginSignIn());
}
